import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { FaChevronDown, FaBars, FaTimes } from "react-icons/fa";
import { IoLogOutOutline } from "react-icons/io5";

import DateBadge from "../DateBadge/DateBadge";
import logoLight from "../logo.png";
import logoDark from "../logo-dark.png";
import ProfileAvatar from "../../assets/images/profile-avatar.png";
import { useThemeMode } from "../../theme/ThemeModeContext";
import { useLogout } from "../../hooks/useLogout";
import LanguageSwitch from "../LanguageSwitch/LanguageSwitch";

const Navbar = ({
  navItems = [],
  profileImage,
  profileModal,
  rightActions,
  drawerActions,
}) => {
  const { t } = useTranslation(["nav", "common"]);
  // Nav items carry an i18nKey ("nav:orders"); the English label stays as the fallback.
  const itemLabel = (item) =>
    item.i18nKey ? t(item.i18nKey, { defaultValue: item.label }) : item.label;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const { resolvedTheme } = useThemeMode();
  const logo = resolvedTheme === "dark" ? logoDark : logoLight;
  const { logout, loggingOut } = useLogout();

  const validProfileImage =
    profileImage &&
    profileImage !== "undefined" &&
    !profileImage.endsWith("/undefined")
      ? profileImage
      : ProfileAvatar;

  return (
    <>
      <header className="sticky top-0 z-[100] border-b border-ui-border bg-ui-surface">
        <div className="w-full px-3 sm:px-4 lg:px-6">
          <div className="flex h-12 items-center justify-between">
            <div className="flex items-center min-w-0">
              {/* Logo */}
              <NavLink
                to="/"
                className="flex items-center flex-shrink-0 mr-3 no-underline"
              >
                <img
                  src={logo}
                  alt={t("nav:logoAlt")}
                  className="h-5 w-auto object-contain"
                />
              </NavLink>

              {/* Desktop Navigation */}
              <nav className="hidden xl:flex items-center gap-1 min-w-0">
                {navItems.map(
                  ({ label, i18nKey, url, icon: Icon, unReadData }, index) => (
                    <NavLink
                      key={index}
                      to={url}
                      className={({ isActive }) =>
                        `relative flex items-center whitespace-nowrap gap-1 px-2 py-1.5 rounded-md text-[13px] xl:text-sm font-medium transition-colors duration-200 no-underline
                    ${
                      isActive
                        ? "bg-brand-light text-brand"
                        : "text-ui-text-muted hover:bg-ui-surface-sunken hover:text-brand"
                    }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {unReadData > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-state-danger px-1 text-[10px] font-semibold text-white">
                              {unReadData > 99 ? "99+" : unReadData}
                            </span>
                          )}

                          {isActive && (
                            <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-brand" />
                          )}

                          <Icon className="w-4 h-4 shrink-0" />
                          <span>{itemLabel({ label, i18nKey })}</span>
                        </>
                      )}
                    </NavLink>
                  ),
                )}
              </nav>
            </div>
            {/* Right Side */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {rightActions}

              <div className="hidden sm:block">
                <LanguageSwitch />
              </div>

              <div className="hidden md:block">
                <DateBadge />
              </div>

              {/* Profile */}
              <div className="relative">
                <button
                  onClick={() =>
                    setModal(modal === "profile" ? null : "profile")
                  }
                  className="flex items-center gap-1 rounded-full border border-ui-border px-1 py-0.5 hover:bg-brand-light transition"
                >
                  <img
                    src={validProfileImage}
                    alt={t("nav:profilePicture")}
                    className="h-7 w-7 rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null; // Prevent infinite loop
                      e.currentTarget.src = ProfileAvatar;
                    }}
                  />

                  <FaChevronDown
                    size={10}
                    className={`hidden sm:block transition-transform ${
                      modal === "profile" ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {modal === "profile" &&
                  profileModal &&
                  profileModal({
                    onClose: () => setModal(null),
                  })}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="xl:hidden rounded-md p-1.5 hover:bg-ui-surface-sunken"
                aria-label={t("nav:toggleMenu")}
              >
                {mobileOpen ? <FaTimes size={16} /> : <FaBars size={16} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer: starts below the sticky header, which sits above it and owns the close button */}
      <div
        className={`fixed inset-x-0 top-12 bottom-0 z-40 xl:hidden transition-all duration-300 ${
          mobileOpen ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/40"
          onClick={() => setMobileOpen(false)}
        />

        {/* Drawer */}
        <div
          className={`absolute right-0 top-0 flex h-full w-72 flex-col bg-ui-surface shadow-ui-md transition-transform duration-300 ease-out ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <nav className="flex-1 overflow-y-auto p-3">
            {navItems.map(
              ({ label, i18nKey, url, icon: Icon, unReadData }, index) => (
                <NavLink
                  key={index}
                  to={url}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `mb-0.5 flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium no-underline ${
                      isActive
                        ? "bg-brand-light text-brand"
                        : "text-ui-text-muted hover:bg-ui-surface-sunken hover:text-brand"
                    }`
                  }
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4 shrink-0" />
                    <span>{itemLabel({ label, i18nKey })}</span>
                  </div>

                  {unReadData > 0 && (
                    <span className="rounded-full bg-state-danger px-2 py-0.5 text-xs text-white">
                      {unReadData > 99 ? "99+" : unReadData}
                    </span>
                  )}
                </NavLink>
              ),
            )}
          </nav>

          <div className="border-t border-ui-border p-3">
            <LanguageSwitch variant="row" className="mb-1" />
            {drawerActions}
            <button
              type="button"
              onClick={logout}
              disabled={loggingOut}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-state-danger hover:bg-ui-surface-sunken disabled:opacity-60"
            >
              <IoLogOutOutline className="w-4 h-4 shrink-0" />
              <span>
                {loggingOut
                  ? t("common:actions.loggingOut")
                  : t("common:actions.logout")}
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
